import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

export function useSelection(allIds) {
  const [selected, setSelected] = useState(() => new Set());
  const visible = new Set(allIds);
  const sel = new Set([...selected].filter((id) => visible.has(id)));
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = allIds.length > 0 && allIds.every((id) => sel.has(id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(allIds));
  const clear = () => setSelected(new Set());
  return { selected: sel, toggle, toggleAll, allSelected, clear, count: sel.size };
}

export function SelectAllBox({ selection, testId = "select-all-checkbox" }) {
  return <input type="checkbox" data-testid={testId} className="h-4 w-4 accent-[#2d7bd0] cursor-pointer" checked={selection.allSelected} onChange={selection.toggleAll} aria-label="Select all" />;
}

export function RowBox({ selection, id }) {
  return <input type="checkbox" data-testid={`select-row-${id}`} className="h-4 w-4 accent-[#2d7bd0] cursor-pointer" checked={selection.selected.has(id)} onChange={() => selection.toggle(id)} onClick={(e) => e.stopPropagation()} aria-label="Select row" />;
}

export function BulkBar({ selection, noun = "item", actions }) {
  if (selection.count === 0) return null;
  return (
    <div className="sticky top-0 z-10 flex items-center gap-2 sm:gap-3 flex-wrap rounded border border-[#2d7bd0]/40 bg-[#e8f1fb] px-3 sm:px-4 py-2 text-sm" data-testid="bulk-bar">
      <span className="font-medium" data-testid="bulk-count">{selection.count} {noun}{selection.count === 1 ? "" : "s"} selected</span>
      <div className="flex items-center gap-2 sm:ml-2 flex-wrap">
        {actions.map((a) => a.options ? (
          <select key={a.label} className="h-8 rounded-md border border-slate-300 bg-white px-2 text-sm" defaultValue="" data-testid={`bulk-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
            onChange={async (e) => {
              const v = e.target.value; e.target.value = "";
              if (!v) return;
              const opt = a.options.find((o) => o.value === v);
              if (a.confirm && !window.confirm(a.confirm.replace("{n}", selection.count).replace("{target}", opt?.label || v))) return;
              await a.onClick([...selection.selected], v);
              selection.clear();
            }}>
            <option value="" disabled>{a.label}</option>
            {a.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <Button key={a.label} size="sm" variant={a.variant || "outline"} className={`h-8 ${a.className || ""}`} data-testid={`bulk-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={async () => {
              if (a.confirm && !window.confirm(a.confirm.replace("{n}", selection.count))) return;
              await a.onClick([...selection.selected]);
              selection.clear();
            }}>
            {a.icon ? <a.icon className="h-3.5 w-3.5 mr-1" /> : null}{a.label}
          </Button>
        ))}
      </div>
      <button type="button" className="ml-auto text-slate-500 hover:text-slate-900" onClick={selection.clear} data-testid="bulk-clear"><X className="h-4 w-4" /></button>
    </div>
  );
}
